import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import videoService from '../../features/video/videoService';
import { assignWitnessLobbyist as assignAction } from '../../features/video/videoSlice';
import { Button } from '../ui/Button';

const WitnessLobbyistAssignment = ({
  witnessId,
  witnessName,
  stateCode,
  currentLobbyistId,
  onAssigned,
}) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const handler = setTimeout(async () => {
      if (!query) {
        setResults([]);
        return;
      }
      try {
        setLoading(true);
        const data = await videoService.searchLobbyists(query, stateCode || 'PA');
        if (!cancelled) setResults(data.data || []);
      } catch (err) {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handler);
    };
  }, [query, open, stateCode]);

  const handleAssign = async (lobbyist) => {
    try {
      setSubmitting(true);
      await dispatch(
        assignAction({
          witnessId,
          lobbyistId: lobbyist.lobbyistId,
          state: stateCode || 'PA',
        })
      ).unwrap();
      setOpen(false);
      setQuery('');
      setResults([]);
      if (onAssigned) onAssigned(lobbyist);
    } catch (err) {
      console.error('Assign failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative inline-block" ref={containerRef}>
      <Button
        variant="ghost"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        className="text-xs text-blue-600 hover:text-blue-800"
        title={
          currentLobbyistId
            ? `Currently assigned to lobbyist #${currentLobbyistId}. Click to change.`
            : 'Assign a lobbyist to this witness'
        }
      >
        {currentLobbyistId ? 'Reassign' : 'Assign Lobbyist'}
      </Button>

      {open && (
        <div
          className="absolute left-0 top-full z-50 mt-2 w-72 rounded-md border border-slate-200 bg-white p-2 shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            autoFocus
            type="text"
            placeholder={`Search lobbyists for ${witnessName}…`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="mb-2 w-full rounded border border-slate-200 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
          <div className="max-h-60 overflow-y-auto">
            {loading && (
              <div className="px-2 py-1 text-xs text-slate-500">Searching…</div>
            )}
            {!loading && query && results.length === 0 && (
              <div className="px-2 py-1 text-xs text-slate-500">No matches.</div>
            )}
            {!loading && !query && (
              <div className="px-2 py-1 text-xs text-slate-400">
                Type to search lobbyists.
              </div>
            )}
            {results.map((l) => (
              <button
                key={l.lobbyistId}
                disabled={submitting}
                onClick={() => handleAssign(l)}
                className="block w-full rounded px-2 py-1 text-left text-sm hover:bg-blue-50"
              >
                <span className="font-medium text-slate-800">{l.name}</span>
                {l.stateCode && (
                  <span className="ml-2 text-xs text-slate-400">{l.stateCode}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default WitnessLobbyistAssignment;
